import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import {
  FaQrcode, FaCheckCircle, FaUser, FaEnvelope, FaPhone,
  FaShieldAlt, FaRedo, FaClock
} from "react-icons/fa";
import ProjectService from "../../services/projectService";
import { toast } from "react-toastify";

// Helper: mask email like r****e@gmail.com
const maskEmail = (email) => {
  if (!email || !email.includes('@')) return email;
  const [local, domain] = email.split('@');
  if (local.length <= 2) return email;
  return local[0] + '*'.repeat(local.length - 2) + local[local.length - 1] + '@' + domain;
};

const ProjectCheckoutModal = ({ show, onHide, project }) => {
  // ── Step State ──────────────────────────────────────────
  // Step 1: Personal Info | Step 2: OTP Verify | Step 3: Payment QR | Step 4: Success
  const [step, setStep] = useState(1);

  // ── Form Data ───────────────────────────────────────────
  const [formData, setFormData] = useState({
    user_name: "",
    user_email: "",
    user_phone: ""
  });

  // ── OTP State ───────────────────────────────────────────
  const [otpRefId, setOtpRefId]     = useState(null);
  const [otpInputs, setOtpInputs]   = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError]     = useState("");
  const [otpShake, setOtpShake]     = useState(false);
  const [resendTimer, setResendTimer] = useState(0);   // seconds
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifying, setIsVerifying]   = useState(false);

  // ── Order State ─────────────────────────────────────────
  const [orderId, setOrderId]       = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const inputRefs = useRef([]);
  const timerRef  = useRef(null);

  // ── Cleanup on modal close ──────────────────────────────
  const resetAll = () => {
    setStep(1);
    setFormData({ user_name: "", user_email: "", user_phone: "" });
    setOtpRefId(null);
    setOtpInputs(["", "", "", "", "", ""]);
    setOtpError("");
    setOtpShake(false);
    setResendTimer(0);
    setIsSendingOtp(false);
    setIsVerifying(false);
    setOrderId(null);
    setIsSubmitting(false);
    clearInterval(timerRef.current);
  };

  const handleClose = () => {
    resetAll();
    onHide();
  };

  // ── Countdown timer for resend ──────────────────────────
  const startResendTimer = (seconds = 30) => {
    setResendTimer(seconds);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  // ── Step 1 → Send OTP ──────────────────────────────────
  const handleNext = async (e) => {
    e.preventDefault();
    if (!formData.user_name.trim() || !formData.user_email.trim()) {
      toast.error("Name and Email are required.");
      return;
    }
    setIsSendingOtp(true);
    try {
      const res = await ProjectService.sendOtp({
        email:      formData.user_email,
        name:       formData.user_name,
        phone:      formData.user_phone,
        project_id: project.id,
        purpose:    "project_order",
      });
      setOtpRefId(res.data.data.otp_ref_id);
      startResendTimer(30);
      setStep(2);
      toast.success("OTP sent to your email!");
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to send OTP. Please try again.";
      toast.error(msg);
    } finally {
      setIsSendingOtp(false);
    }
  };

  // ── OTP digit input handling ────────────────────────────
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;           // digits only
    const newInputs = [...otpInputs];
    newInputs[index] = value.slice(-1);          // keep only last char
    setOtpInputs(newInputs);
    setOtpError("");
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpInputs[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setOtpInputs(pasted.split(""));
      inputRefs.current[5]?.focus();
    }
  };

  const triggerShake = () => {
    setOtpShake(true);
    setTimeout(() => setOtpShake(false), 500);
  };

  // ── Step 2 → Verify OTP ────────────────────────────────
  const handleVerifyOtp = async () => {
    const code = otpInputs.join("");
    if (code.length !== 6) {
      setOtpError("Please enter the 6-digit code.");
      triggerShake();
      return;
    }
    setIsVerifying(true);
    setOtpError("");
    try {
      const res = await ProjectService.verifyOtp({ otp_ref_id: otpRefId, otp_code: code });
      setOrderId(res.data.data.order_id);
      clearInterval(timerRef.current);
      setStep(3);  // Move to Payment QR
    } catch (err) {
      const msg = err?.response?.data?.message || "Incorrect OTP. Please try again.";
      setOtpError(msg);
      triggerShake();
      setOtpInputs(["", "", "", "", "", ""]);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } finally {
      setIsVerifying(false);
    }
  };

  // ── Resend OTP ─────────────────────────────────────────
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    setIsSendingOtp(true);
    setOtpError("");
    setOtpInputs(["", "", "", "", "", ""]);
    try {
      const res = await ProjectService.sendOtp({
        email:      formData.user_email,
        name:       formData.user_name,
        phone:      formData.user_phone,
        project_id: project.id,
        purpose:    "project_order",
      });
      setOtpRefId(res.data.data.otp_ref_id);
      startResendTimer(30);
      toast.success("A new OTP has been sent!");
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to resend OTP.";
      toast.error(msg);
    } finally {
      setIsSendingOtp(false);
    }
  };

  // ── Step 3 → Submit (send payment screenshot email) ────
  const handleSubmit = async () => {
    setIsSubmitting(true);
    const subject = encodeURIComponent(`Payment Screenshot for Project: ${project.title}`);
    const body = encodeURIComponent(
      `Hello Admin,\n\nI have just made the payment of ₹${project.price} for the project "${project.title}".\n\nPlease find the payment screenshot attached to this email.\n\nMy Details:\nName: ${formData.user_name}\nEmail: ${formData.user_email}\nPhone: ${formData.user_phone}\nOrder ID: ${orderId}\n\nThank you.`
    );
    window.location.href = `mailto:sensibleacademy@gmail.com?subject=${subject}&body=${body}`;
    toast.success("Order submitted! Please attach your screenshot in the email.");
    setIsSubmitting(false);
    setStep(4);
  };

  if (!project) return null;

  const upiString  = `upi://pay?pa=9301351989@ybl&pn=Admin&am=${project.price}`;
  const qrCodeUrl  = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(upiString)}`;
  const otpFull    = otpInputs.join("");

  return (
    <>
      {/* Shake animation */}
      <style>{`
        @keyframes shake {
          0%,100% { transform: translateX(0); }
          20%,60%  { transform: translateX(-6px); }
          40%,80%  { transform: translateX(6px); }
        }
        .otp-shake { animation: shake 0.4s ease; }
        .otp-digit-input {
          width: 44px; height: 54px; font-size: 22px; font-weight: 700;
          text-align: center; border: 2px solid #d1d5db; border-radius: 10px;
          outline: none; transition: border-color 0.2s, box-shadow 0.2s;
          background: #f9fafb; color: #111827;
        }
        .otp-digit-input:focus { border-color: #4f46e5; box-shadow: 0 0 0 3px rgba(79,70,229,0.15); background: #fff; }
        .otp-digit-input.has-value { border-color: #4f46e5; background: #eef2ff; }
        .otp-digit-input.error { border-color: #ef4444 !important; }
      `}</style>

      <Modal show={show} onHide={handleClose} centered backdrop="static">
        <Modal.Header closeButton={step !== 4}>
          <Modal.Title className="fw-semibold">
            {step === 1 && "Your Details"}
            {step === 2 && <><FaShieldAlt className="me-2 text-primary" size={18} />Verify Your Email</>}
            {step === 3 && "Make Payment"}
            {step === 4 && "Order Confirmed!"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4">

          {/* ── STEP 1: Personal Info ───────────────────────── */}
          {step === 1 && (
            <Form onSubmit={handleNext}>
              <div className="text-center mb-4 p-3 rounded-3" style={{ background: "linear-gradient(135deg,#eef2ff,#f0fdf4)" }}>
                <p className="text-muted mb-1 small">You are buying</p>
                <h6 className="fw-bold mb-1">{project.title}</h6>
                <h4 className="fw-bold text-success mb-0">₹{project.price}</h4>
              </div>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small"><FaUser className="me-2 text-muted" />Full Name</Form.Label>
                <Form.Control
                  type="text" name="user_name" required
                  value={formData.user_name}
                  onChange={e => setFormData({ ...formData, user_name: e.target.value })}
                  placeholder="Enter your full name"
                  style={{ borderRadius: 10, padding: "10px 14px" }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small"><FaEnvelope className="me-2 text-muted" />Email Address</Form.Label>
                <Form.Control
                  type="email" name="user_email" required
                  value={formData.user_email}
                  onChange={e => setFormData({ ...formData, user_email: e.target.value })}
                  placeholder="Email to receive project files"
                  style={{ borderRadius: 10, padding: "10px 14px" }}
                />
                <Form.Text className="text-muted">We'll send a verification code to this email.</Form.Text>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label className="fw-semibold small"><FaPhone className="me-2 text-muted" />Phone Number</Form.Label>
                <Form.Control
                  type="text" name="user_phone"
                  value={formData.user_phone}
                  onChange={e => setFormData({ ...formData, user_phone: e.target.value })}
                  placeholder="Optional"
                  style={{ borderRadius: 10, padding: "10px 14px" }}
                />
              </Form.Group>

              <Button
                variant="primary" type="submit" className="w-100 fw-bold py-2"
                style={{ borderRadius: 10 }} disabled={isSendingOtp}
              >
                {isSendingOtp ? <><Spinner size="sm" className="me-2" />Sending OTP...</> : "Next — Verify Email"}
              </Button>
            </Form>
          )}

          {/* ── STEP 2: OTP Verification ────────────────────── */}
          {step === 2 && (
            <div>
              <div className="text-center mb-4">
                <div className="mb-3" style={{ fontSize: 48 }}>📬</div>
                <h6 className="fw-bold mb-1">Check your inbox</h6>
                <p className="text-muted small mb-0">
                  We sent a 6-digit code to<br />
                  <strong>{maskEmail(formData.user_email)}</strong>
                </p>
              </div>

              {/* OTP digit boxes */}
              <div
                className={`d-flex justify-content-center gap-2 mb-3 ${otpShake ? "otp-shake" : ""}`}
                onPaste={handleOtpPaste}
              >
                {otpInputs.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => inputRefs.current[i] = el}
                    type="text" inputMode="numeric" maxLength={1}
                    value={digit}
                    onChange={e => handleOtpChange(i, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(i, e)}
                    className={`otp-digit-input ${digit ? "has-value" : ""} ${otpError ? "error" : ""}`}
                  />
                ))}
              </div>

              {/* Error message */}
              {otpError && (
                <p className="text-danger text-center small mb-3">{otpError}</p>
              )}

              {/* Timer */}
              <div className="text-center mb-4">
                {resendTimer > 0 ? (
                  <span className="text-muted small">
                    <FaClock className="me-1" />
                    Resend in {resendTimer}s
                  </span>
                ) : (
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-decoration-none p-0"
                    onClick={handleResendOtp}
                    disabled={isSendingOtp}
                  >
                    {isSendingOtp ? <><Spinner size="sm" className="me-1" />Sending...</> : <><FaRedo className="me-1" />Resend OTP</>}
                  </button>
                )}
              </div>

              <Row>
                <Col>
                  <Button
                    variant="outline-secondary" className="w-100" style={{ borderRadius: 10 }}
                    onClick={() => setStep(1)} disabled={isVerifying}
                  >
                    Back
                  </Button>
                </Col>
                <Col>
                  <Button
                    variant="primary" className="w-100 fw-bold" style={{ borderRadius: 10 }}
                    onClick={handleVerifyOtp}
                    disabled={isVerifying || otpFull.length !== 6}
                  >
                    {isVerifying ? <><Spinner size="sm" className="me-2" />Verifying...</> : "Verify OTP"}
                  </Button>
                </Col>
              </Row>
            </div>
          )}

          {/* ── STEP 3: Payment QR ──────────────────────────── */}
          {step === 3 && (
            <div className="text-center">
              <div className="mb-2">
                <FaCheckCircle className="text-success" size={28} />
                <span className="fw-semibold text-success ms-2">Email Verified!</span>
              </div>
              <h5 className="mb-3">Scan to Pay ₹{project.price}</h5>
              <div className="p-3 bg-light border rounded mb-3 d-inline-block">
                <img src={qrCodeUrl} alt="UPI QR Code" style={{ width: 200, height: 200 }} />
              </div>
              <p className="text-muted small mb-2">
                UPI ID: <strong>9301351989@ybl</strong>
              </p>
              <hr />
              <div className="text-start mb-4">
                <p className="mb-1 fw-bold">Next Steps:</p>
                <ol className="small text-muted ps-3">
                  <li>Scan the QR code and complete the UPI payment.</li>
                  <li>Take a screenshot of the successful transaction.</li>
                  <li>Click Submit below — your email client will open.</li>
                  <li>Attach the screenshot to the email and send it.</li>
                </ol>
              </div>
              <Row>
                <Col>
                  <Button
                    variant="outline-secondary" className="w-100" style={{ borderRadius: 10 }}
                    onClick={() => setStep(2)} disabled={isSubmitting}
                  >
                    Back
                  </Button>
                </Col>
                <Col>
                  <Button
                    variant="success" className="w-100 fw-bold" style={{ borderRadius: 10 }}
                    onClick={handleSubmit} disabled={isSubmitting}
                  >
                    {isSubmitting ? "Processing..." : "Submit & Email Screenshot"}
                  </Button>
                </Col>
              </Row>
            </div>
          )}

          {/* ── STEP 4: Success ─────────────────────────────── */}
          {step === 4 && (
            <div className="text-center py-3">
              <FaCheckCircle className="text-success mb-3" size={64} />
              <h4 className="fw-bold mb-2">Request Sent!</h4>
              <p className="text-muted mb-1">
                If your email client didn't open automatically, please email your
                payment screenshot to <strong>sensibleacademy@gmail.com</strong>.
              </p>
              <p className="text-muted small mt-3">
                Once we verify your payment, we will email you the project ZIP file within 24 hours.
              </p>
              
              <div className="d-flex justify-content-center gap-2 mt-4">
                <Button 
                  variant="outline-secondary" 
                  className="px-4 fw-bold d-flex align-items-center" 
                  style={{ borderRadius: 10 }}
                  onClick={() => setStep(3)}
                >
                  <FaQrcode className="me-2" /> Show QR Code
                </Button>

                <Button
                  variant="primary" 
                  className="px-4 fw-bold" 
                  style={{ borderRadius: 10 }}
                  onClick={handleClose}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ProjectCheckoutModal;
